/**
 * The ontology kinds a customer can pick for a custom object type.
 *
 * `entity_kind` answers "what is this thing?" — it is the classification the
 * rest of the platform reasons over. A type that carries only the supporting
 * `default` kind has no answer, and that has consequences well beyond the UI:
 * it is excluded from the Snowflake export (`EXPORTABLE_ENTITY_KINDS`), it
 * matches no kind-based association endpoint (`source_entity_kind_name`), and
 * autonomy rules that switch on kind skip it. The object-type wizard used to
 * stamp `['default']` on every non-pipelineable custom type, so every one of
 * them was created anonymous.
 *
 * Two deliberate constraints on this list:
 *
 * 1. `principal` is NOT offered. Principals are platform actors (users, agents,
 *    automations) — a customer never authors one.
 * 2. The labels carry NO engine vocabulary. `docs/claude/ENTITY_KINDS.md` calls
 *    these Party / Case / Asset / Activity, and the UX copy rules name `case`
 *    and `asset` explicitly as internal ontology that must not reach a customer
 *    string. So the `value` is the schema identifier and the `label` is a plain
 *    description of the thing; the two never have to match.
 *
 * Each kind composes with `default` (see `composable_with` in the backend's
 * `schema_registry/entity_kinds.py`), so a type is always stamped
 * `['default', <kind>]`. Only `case` injects extra base properties; the other
 * three are pure markers.
 *
 * TODO(copy): labels and hints below are a first pass and need Laura's review —
 * they name concepts the product has not had customer-facing words for before.
 */

/** Schema identifiers. Not copy — these are written to `ahoy_entity_kind`. */
export type OntologyKind = 'party' | 'case' | 'asset' | 'activity';

export interface OntologyKindOption {
  /** The value stamped into `ahoy_entity_kind` alongside `default`. */
  value: OntologyKind;
  /** Plain-language name for the thing. Sentence case — this is a choice, not a term. */
  label: string;
  /** One line of examples, to make the choice concrete. */
  hint: string;
}

/**
 * Ordered for recognition, not for ontology: the two kinds a CRM customer is
 * most likely to be modelling come first.
 */
export const ONTOLOGY_KIND_OPTIONS: readonly OntologyKindOption[] = [
  {
    value: 'party',
    label: 'A person or company',
    hint: 'Like a supplier, a partner, or a candidate.',
  },
  {
    value: 'case',
    label: 'Work you take to a finish',
    hint: 'Moves through stages. Like an order, a ticket, or a renewal.',
  },
  {
    value: 'asset',
    label: 'Something you own or manage',
    hint: 'Like a subscription, a property, or a device.',
  },
  {
    value: 'activity',
    label: 'A record of something that happened',
    hint: 'Like a site visit or an inspection.',
  },
];

/**
 * The plain-language label for a kind, as the customer chose it.
 *
 * Keeps kind → label in one place so a review summary and the picker can never
 * describe the same choice differently. `null` is unreachable from the wizard
 * (the Type step gates advancing on a choice) but is typed because the caller
 * holds the pre-choice state.
 */
export function getOntologyKindLabel(kind: OntologyKind | null): string {
  if (kind === null) {
    return 'Not set';
  }
  return ONTOLOGY_KIND_OPTIONS.find(o => o.value === kind)?.label ?? 'Not set';
}

/**
 * The kind that rides a pipeline of stages.
 *
 * Pipelines are the shape of this kind, not an independent capability: before
 * the wizard asked the question, toggling "pipelineable" was the only way to
 * get a type stamped `case`, so `case ⟺ has a pipeline` was already an
 * invariant. Naming it here keeps that invariant in one place instead of
 * spread across the wizard's step gating and the event builder.
 */
export const PIPELINE_ONTOLOGY_KIND: OntologyKind = 'case';

/** Whether a chosen kind rides a pipeline (and so needs the Pipeline step). */
export function kindRidesPipeline(
  kind: OntologyKind | null
): kind is typeof PIPELINE_ONTOLOGY_KIND {
  return kind === PIPELINE_ONTOLOGY_KIND;
}
